import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input, Label, Textarea } from '@/components/ui/input';
import { api } from '@/lib/api';
import type { Account } from '@/types/account';

// Placeholder shown in the cookies box — the exact shape a browser cookie
// export produces, with the sensitive values replaced by `<your …>` so no real
// session token lives in the source. The operator pastes their own export over
// it (the login flow tries these cookies first, skipping the password/captcha).
const EXAMPLE_COOKIES_JSON = `[
  {
    "name": "sessionid",
    "value": "<your sessionid>",
    "domain": ".instagram.com",
    "path": "/",
    "httpOnly": true,
    "secure": true,
    "sameSite": "Lax"
  },
  {
    "name": "csrftoken",
    "value": "<your csrftoken>",
    "domain": ".instagram.com",
    "path": "/",
    "secure": true,
    "sameSite": "Lax"
  },
  {
    "name": "ds_user_id",
    "value": "<your ds_user_id>",
    "domain": ".instagram.com",
    "path": "/",
    "secure": true,
    "sameSite": "Lax"
  },
  {
    "name": "mid",
    "value": "<your mid>",
    "domain": ".instagram.com",
    "path": "/",
    "secure": true,
    "sameSite": "Lax"
  },
  {
    "name": "ig_did",
    "value": "<your ig_did>",
    "domain": ".instagram.com",
    "path": "/",
    "secure": true,
    "sameSite": "Lax"
  }
]`;

/** Parse + validate the pasted cookies JSON. Returns the cookie array, or null
 *  when the box is empty. Throws a friendly message on anything invalid. */
function parseCookies(text: string): Record<string, unknown>[] | null {
  const trimmed = text.trim();
  if (!trimmed) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    throw new Error('Session cookies must be valid JSON — an array of cookie objects.');
  }
  if (!Array.isArray(parsed) || parsed.length === 0) {
    throw new Error('Session cookies must be a non-empty JSON array.');
  }

  const sessionCookie = parsed.find(
    (c): c is Record<string, unknown> =>
      !!c && typeof c === 'object' && (c as Record<string, unknown>).name === 'sessionid',
  );
  const raw = sessionCookie?.value;
  const value = typeof raw === 'string' ? raw : '';
  if (!sessionCookie || value.length < 10 || value.startsWith('<')) {
    throw new Error(
      'Cookies must include a real "sessionid" — replace the <your …> placeholders with your account’s values.',
    );
  }
  return parsed as Record<string, unknown>[];
}

export function LinkAccountForm({
  personaId,
  account,
  onLinked,
  onCancel,
}: {
  personaId: string;
  /** When set, connect this existing account (re-supply a session) instead of
   *  creating a new one — used after a Disconnect wiped the stored session. */
  account?: Account;
  onLinked: (workflowId: string) => void;
  onCancel?: () => void;
}) {
  const queryClient = useQueryClient();
  const isExisting = !!account;
  const [handle, setHandle] = useState(account?.handle ?? '');
  const [cookiesText, setCookiesText] = useState('');

  const m = useMutation({
    mutationFn: async () => {
      // Throws a friendly message (→ onError toast) if the JSON is malformed.
      const cookies = parseCookies(cookiesText);
      if (!cookies) {
        throw new Error('Paste your session cookies to attach the account.');
      }
      const credentials = {
        // Stored handle doubles as the login username — cookie auth doesn't use
        // it, but the API requires a username field.
        username: handle.trim().replace(/^@/, ''),
        cookies,
      };
      if (account) {
        return api.connectAccount(account.account_id, credentials);
      }
      return api.linkAccount(personaId, {
        platform: 'instagram',
        handle: handle.trim().replace(/^@/, ''),
        credentials,
      });
    },
    onSuccess: (res) => {
      toast.success('Attaching account — validating the session…');
      void queryClient.invalidateQueries({ queryKey: ['persona', personaId, 'accounts'] });
      onLinked(res.workflow_id);
    },
    onError: (err) => toast.error('Attach failed', { description: String(err) }),
  });

  const canSubmit = !!handle.trim() && !!cookiesText.trim();

  return (
    <Card>
      <CardHeader>
        <CardTitle>{isExisting ? `Attach @${account?.handle}` : 'Attach Instagram account'}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="handle">Instagram handle (without @)</Label>
          <Input
            id="handle"
            value={handle}
            onChange={(e) => setHandle(e.target.value)}
            placeholder="example_username"
            autoComplete="off"
            disabled={isExisting}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="cookies">Session cookies (JSON)</Label>
          <Textarea
            id="cookies"
            value={cookiesText}
            onChange={(e) => setCookiesText(e.target.value)}
            placeholder={EXAMPLE_COOKIES_JSON}
            rows={12}
            spellCheck={false}
            className="font-mono text-xs"
          />
          <div className="rounded-md border border-border bg-muted/30 p-3 text-xs text-muted-foreground">
            <p className="font-medium text-foreground">How to get your cookies (≈1 min):</p>
            <ol className="mt-1 list-decimal space-y-1 pl-4">
              <li>
                In Chrome, log into{' '}
                <span className="font-medium text-foreground">this exact Instagram account</span>{' '}
                (ideally from a residential / home connection).
              </li>
              <li>
                Install the{' '}
                <a
                  href="https://chromewebstore.google.com/detail/cookie-exporter/fhnmmidekmgocpjdceeffppcodigillk"
                  target="_blank"
                  rel="noreferrer"
                  className="font-medium text-foreground underline underline-offset-2"
                >
                  Cookie Exporter
                </a>{' '}
                Chrome extension.
              </li>
              <li>
                Open <code>instagram.com</code>, then click the{' '}
                <span className="font-medium text-foreground">Cookie Exporter</span> extension icon.
              </li>
              <li>
                Set <span className="font-medium text-foreground">Export format: JSON</span>, then
                click <span className="font-medium text-foreground">Copy</span>.
              </li>
              <li>Paste it into the box above (replacing the placeholder).</li>
            </ol>
            <p className="mt-2">
              Must include <code>sessionid</code>, <code>csrftoken</code>, and <code>ds_user_id</code>.
              Ghost validates the session and stores it AES-256 encrypted. When the account later
              shows <span className="font-medium">“Session expired”</span>, repeat these steps to
              re-paste a fresh export.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2">
          {onCancel && (
            <Button variant="ghost" onClick={onCancel} disabled={m.isPending}>
              Cancel
            </Button>
          )}
          <Button onClick={() => m.mutate()} disabled={!canSubmit || m.isPending}>
            {m.isPending ? 'Attaching…' : 'Attach account'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
